import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Film, Layers, Upload, Wand2, Clapperboard, Rocket, Zap,
  Check, ChevronRight, ArrowRight, ArrowLeft,
  Loader2, Coins, AlertTriangle, Cpu, Download, Save,
  Send, Monitor, Smartphone, Square, Clock, Play, Eye,
  FileText, Image, Mic, Subtitles, Sparkles, Merge, Trash2, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════ CONSTANTS ═══════════════════ */

const STEPS = [
  { id: 1, label: 'Video Modu', short: 'Mod' },
  { id: 2, label: 'Platform & Ayarlar', short: 'Ayar' },
  { id: 3, label: 'AI Modelleri', short: 'AI' },
  { id: 4, label: 'Detaylar', short: 'Detay' },
  { id: 5, label: 'Üretim & İzleme', short: 'Üretim' },
  { id: 6, label: 'Önizleme', short: 'Önizle' },
  { id: 7, label: 'İndirme & Paylaşım', short: 'Paylaş' },
];

const VIDEO_MODES = [
  { id: 'kling_t2v', label: 'Kling AI Video', desc: 'Konu → Kling AI ile yüksek kalite video', icon: Zap, color: 'violet', badge: 'Kling AI' },
  { id: 'kling_i2v', label: 'Kling Görsel → Video', desc: 'Görsel yükle → Kling AI ile canlandır', icon: Upload, color: 'green', badge: 'Kling I2V' },
  { id: 'runway', label: 'Runway AI Video', desc: 'Konu → AI görsel → Sinematik video', icon: Film, color: 'blue', badge: 'Runway' },
  { id: 'runway_batch', label: 'Toplu Klip Üretimi', desc: 'Birden fazla sahne → Klip → Birleştirme', icon: Layers, color: 'cyan', badge: 'Çoklu Sahne' },
  { id: 'runway_i2v', label: 'Runway Görsel Canlandır', desc: 'Görsel yükle → Runway ile animate', icon: Upload, color: 'amber', badge: 'Runway I2V' },
  { id: 'runway_v2v', label: 'Video Dönüştürme', desc: 'Mevcut videoyu AI ile dönüştürün', icon: Wand2, color: 'rose', badge: 'Dönüşüm' },
  { id: 'pipeline', label: 'AI Pipeline', desc: 'Senaryo + Görsel + Ses + Altyazı + FFmpeg', icon: Clapperboard, color: 'cyan', badge: 'Tam Paket' },
  { id: 'merge', label: 'Video Birleştirme', desc: 'Mevcut videoları seçin ve birleştirin', icon: Merge, color: 'amber', badge: 'FFmpeg' },
];

const PLATFORMS = [
  { value: 'instagram_reel', label: 'Instagram Reels', ratio: '9:16', icon: Smartphone },
  { value: 'tiktok', label: 'TikTok', ratio: '9:16', icon: Smartphone },
  { value: 'youtube_short', label: 'YouTube Shorts', ratio: '9:16', icon: Smartphone },
  { value: 'youtube', label: 'YouTube', ratio: '16:9', icon: Monitor },
  { value: 'square', label: 'Kare (1:1)', ratio: '1:1', icon: Square },
];

const TONES = ['Profesyonel', 'Günlük', 'Esprili', 'İlham Verici', 'Eğitici', 'Cesur'];

const VOICES = [
  { id: 'nova', label: 'Nova (Kadın)' },
  { id: 'alloy', label: 'Alloy (Nötr)' },
  { id: 'echo', label: 'Echo (Erkek)' },
  { id: 'fable', label: 'Fable (Anlatıcı)' },
  { id: 'onyx', label: 'Onyx (Derin)' },
  { id: 'shimmer', label: 'Shimmer (Sıcak)' },
];

const colorMap = {
  violet: 'from-violet-500/20 to-violet-600/5 border-violet-500/30 text-violet-400',
  blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/30 text-blue-400',
  green: 'from-green-500/20 to-green-600/5 border-green-500/30 text-green-400',
  amber: 'from-amber-500/20 to-amber-600/5 border-amber-500/30 text-amber-400',
  rose: 'from-rose-500/20 to-rose-600/5 border-rose-500/30 text-rose-400',
  cyan: 'from-cyan-500/20 to-cyan-600/5 border-cyan-500/30 text-cyan-400',
};

/* ═══════════════════ STEPPER ═══════════════════ */

function Stepper({ currentStep, steps, onStepClick }) {
  return (
    <div className="flex items-center gap-1 w-full overflow-x-auto pb-2" data-testid="video-wizard-stepper">
      {steps.map((step, i) => {
        const isActive = currentStep === step.id;
        const isDone = currentStep > step.id;
        return (
          <div key={step.id} className="flex items-center flex-shrink-0">
            <button
              onClick={() => isDone && onStepClick(step.id)}
              disabled={!isDone}
              data-testid={`video-step-${step.id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-ibm transition-all ${
                isActive
                  ? 'bg-amber-600/20 border border-amber-500/30 text-amber-300'
                  : isDone
                    ? 'bg-green-500/10 border border-green-500/20 text-green-400 cursor-pointer hover:bg-green-500/20'
                    : 'bg-[#141414] border border-[#1A1A1A] text-[#525252]'
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                isActive ? 'bg-amber-600 text-white' : isDone ? 'bg-green-500 text-white' : 'bg-[#262626] text-[#525252]'
              }`}>
                {isDone ? <Check size={10} /> : step.id}
              </div>
              <span className="hidden sm:inline">{step.short}</span>
            </button>
            {i < steps.length - 1 && <ChevronRight size={12} className="text-[#262626] mx-0.5 flex-shrink-0" />}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════ STEP 1: VIDEO MODE ═══════════════════ */

function Step1({ selectedMode, setSelectedMode }) {
  return (
    <div className="space-y-6" data-testid="video-step-mode">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">Nasıl bir video üretmek istiyorsunuz?</h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">Video üretim modunu seçin</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {VIDEO_MODES.map(mode => {
          const selected = selectedMode === mode.id;
          return (
            <button
              key={mode.id}
              onClick={() => setSelectedMode(mode.id)}
              data-testid={`video-mode-${mode.id}`}
              className={`relative p-5 rounded-xl border text-left transition-all ${
                selected
                  ? `bg-gradient-to-br ${colorMap[mode.color]} border`
                  : 'bg-[#0F0F0F] border-[#1A1A1A] hover:border-[#333] text-[#A3A3A3]'
              }`}
            >
              {selected && <div className="absolute top-3 right-3"><Check size={14} className="text-green-400" /></div>}
              <div className="flex items-center gap-3 mb-2">
                <mode.icon size={22} className={selected ? '' : 'text-[#525252]'} />
                <Badge variant="outline" className={`text-[9px] ${selected ? 'border-current opacity-80' : 'border-[#262626] text-[#525252]'}`}>
                  {mode.badge}
                </Badge>
              </div>
              <p className={`font-ibm font-medium text-sm ${selected ? 'text-white' : ''}`}>{mode.label}</p>
              <p className="text-xs text-[#525252] mt-1 leading-relaxed">{mode.desc}</p>
            </button>
          );
        })}
      </div>
      {/* Mode Info Cards */}
      {selectedMode && (
        <Card className="bg-[#0A0A0A] border-[#1A1A1A]">
          <CardContent className="p-4">
            <p className="text-xs text-[#525252] font-ibm uppercase tracking-wider mb-2">Seçili Mod Hakkında</p>
            {selectedMode === 'kling_t2v' && <p className="text-sm text-[#A3A3A3] font-ibm">Konunuzu yazın, Kling AI v2 doğrudan yüksek kaliteli video üretir. OpenAI gerekmez, tamamen Kling AI ile çalışır.</p>}
            {selectedMode === 'kling_i2v' && <p className="text-sm text-[#A3A3A3] font-ibm">Kendi görselinizi yükleyin, Kling AI onu profesyonel bir videoya dönüştürsün. Ürün tanıtımları için ideal.</p>}
            {selectedMode === 'runway' && <p className="text-sm text-[#A3A3A3] font-ibm">AI, konunuza uygun bir görsel oluşturur ve bu görseli sinematik bir videoya dönüştürür. Tek sahnelik, kısa ve etkileyici videolar için idealdir.</p>}
            {selectedMode === 'runway_batch' && <p className="text-sm text-[#A3A3A3] font-ibm">Birden fazla sahne oluşturulur, her biri ayrı video klibine dönüştürülür ve otomatik olarak birleştirilir. Hikaye anlatımı için idealdir.</p>}
            {selectedMode === 'runway_i2v' && <p className="text-sm text-[#A3A3A3] font-ibm">Kendi görselinizi yükleyin ve AI onu hareketli bir videoya dönüştürsün. Ürün tanıtımları ve sosyal medya için idealdir.</p>}
            {selectedMode === 'runway_v2v' && <p className="text-sm text-[#A3A3A3] font-ibm">Mevcut bir videoyu AI ile yeniden stilize edin. Renk düzeltmesi, efekt ekleme ve atmosfer değişikliği yapabilirsiniz.</p>}
            {selectedMode === 'campaign' && <p className="text-sm text-[#A3A3A3] font-ibm">Tek bir konudan Instagram Reels, TikTok, YouTube Shorts, YouTube ve Kare formatında 5 ayrı video üretilir. Çoklu platform stratejisi için idealdir.</p>}
            {selectedMode === 'pipeline' && <p className="text-sm text-[#A3A3A3] font-ibm">Senaryo yazımı, görsel üretimi, seslendirme, altyazı ve FFmpeg montajı dahil tam bir video üretim hattı. En kapsamlı moddur.</p>}
            {selectedMode === 'merge' && <p className="text-sm text-[#A3A3A3] font-ibm">Kütüphanenizdeki veya dışarıdan eklediğiniz videoları sıralayıp tek bir videoda birleştirin. AI gerekmez, doğrudan FFmpeg ile işlenir.</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ═══════════════════ STEP 2: PLATFORM & SETTINGS ═══════════════════ */

function Step2({ selectedMode, form, setForm, providers }) {
  const isKling = selectedMode.startsWith('kling');
  const isMerge = selectedMode === 'merge';
  const showPlatform = !['campaign', 'merge'].includes(selectedMode);
  const showRatio = ['runway', 'runway_batch', 'runway_i2v', 'runway_v2v'].includes(selectedMode);
  const showDuration = ['runway', 'runway_batch', 'runway_i2v'].includes(selectedMode);
  const showClipCount = selectedMode === 'runway_batch';
  const showSceneCount = selectedMode === 'pipeline';
  const showVoice = selectedMode === 'pipeline';

  return (
    <div className="space-y-6" data-testid="video-step-settings">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">
          {selectedMode === 'campaign' ? 'Kampanya Ayarları' : 'Platform ve Video Ayarları'}
        </h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">
          {selectedMode === 'campaign' ? 'Kampanya otomatik olarak 5 platforma video üretir' : 'Hedef platformu ve teknik parametreleri belirleyin'}
        </p>
      </div>

      {selectedMode === 'campaign' && (
        <Card className="bg-[#141414] border-[#262626]">
          <CardContent className="p-4">
            <p className="text-xs text-[#525252] font-ibm uppercase tracking-wider mb-3">Kampanya Platformları</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                { name: 'Instagram Reels', ratio: '9:16' },
                { name: 'TikTok', ratio: '9:16' },
                { name: 'YouTube Shorts', ratio: '9:16' },
                { name: 'YouTube', ratio: '16:9' },
                { name: 'Kare', ratio: '1:1' },
              ].map(p => (
                <div key={p.name} className="p-3 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A] text-center">
                  <p className="text-xs text-white font-ibm font-medium">{p.name}</p>
                  <p className="text-[10px] text-[#525252] mt-0.5">{p.ratio}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {isMerge && (
        <Card className="bg-[#141414] border-[#262626]">
          <CardContent className="p-4">
            <p className="text-xs text-[#525252] font-ibm uppercase tracking-wider mb-3">Birleştirme Ayarları</p>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Çıkış En-Boy Oranı</Label>
              <Select value={form.merge_aspect_ratio || '16:9'} onValueChange={v => setForm(prev => ({ ...prev, merge_aspect_ratio: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="merge-ratio-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="16:9">Yatay (16:9)</SelectItem>
                  <SelectItem value="9:16">Dikey (9:16)</SelectItem>
                  <SelectItem value="1:1">Kare (1:1)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {showPlatform && (
        <div>
          <p className="text-xs text-[#525252] font-ibm mb-2 uppercase tracking-wider">Hedef Platform</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {PLATFORMS.map(p => {
              const selected = form.platform === p.value;
              return (
                <button
                  key={p.value}
                  onClick={() => setForm(prev => ({ ...prev, platform: p.value }))}
                  data-testid={`video-platform-${p.value}`}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    selected ? 'bg-amber-500/10 border-amber-500/30' : 'bg-[#0A0A0A] border-[#1A1A1A] hover:border-[#333]'
                  }`}
                >
                  <p.icon size={18} className={`mx-auto mb-1 ${selected ? 'text-amber-400' : 'text-[#525252]'}`} />
                  <p className={`text-xs font-ibm ${selected ? 'text-white' : 'text-[#737373]'}`}>{p.label}</p>
                  <p className="text-[10px] text-[#525252] mt-0.5">{p.ratio}</p>
                  {selected && <Check size={10} className="mx-auto mt-1 text-green-400" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {showRatio && (
          <div>
            <Label className="text-[#A3A3A3] font-ibm text-sm">Video Oranı (Runway)</Label>
            <Select value={form.runway_ratio} onValueChange={v => setForm(prev => ({ ...prev, runway_ratio: v }))}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="video-ratio-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                {(providers?.video?.ratios || [
                  { id: '1280:720', label: '16:9 (Yatay)' },
                  { id: '720:1280', label: '9:16 (Dikey)' },
                  { id: '1080:1080', label: '1:1 (Kare)' },
                ]).map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {showDuration && (
          <div>
            <Label className="text-[#A3A3A3] font-ibm text-sm">Video Süresi</Label>
            <Select value={String(form.runway_duration)} onValueChange={v => setForm(prev => ({ ...prev, runway_duration: parseInt(v) }))}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="video-duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                <SelectItem value="5">5 saniye</SelectItem>
                <SelectItem value="10">10 saniye</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {showClipCount && (
          <div>
            <Label className="text-[#A3A3A3] font-ibm text-sm">Klip Sayısı</Label>
            <Select value={String(form.clip_count)} onValueChange={v => setForm(prev => ({ ...prev, clip_count: parseInt(v) }))}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="video-clip-count">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                {['2', '3', '4', '5'].map(n => <SelectItem key={n} value={n}>{n} klip</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {showSceneCount && (
          <div>
            <Label className="text-[#A3A3A3] font-ibm text-sm">Sahne Sayısı</Label>
            <Select value={String(form.scene_count)} onValueChange={v => setForm(prev => ({ ...prev, scene_count: parseInt(v) }))}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="video-scene-count">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                {['2', '3', '4', '5'].map(n => <SelectItem key={n} value={n}>{n} sahne</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {showVoice && (
          <div>
            <Label className="text-[#A3A3A3] font-ibm text-sm">Seslendirme</Label>
            <Select value={form.voice} onValueChange={v => setForm(prev => ({ ...prev, voice: v }))}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="video-voice-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                {VOICES.map(v => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {isKling && (
          <>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Kling Model</Label>
              <Select value={form.kling_model} onValueChange={v => setForm(prev => ({ ...prev, kling_model: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="kling-model-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="kling-v3">Kling v3 (En Yeni)</SelectItem>
                  <SelectItem value="kling-v2-6">Kling v2.6</SelectItem>
                  <SelectItem value="kling-v2-1-master">Kling v2.1 Master</SelectItem>
                  <SelectItem value="kling-v2-master">Kling v2 Master</SelectItem>
                  <SelectItem value="kling-v1-6">Kling v1.6</SelectItem>
                  <SelectItem value="kling-v1-5">Kling v1.5 (Sadece Pro)</SelectItem>
                  <SelectItem value="kling-v1">Kling v1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Süre</Label>
              <Select value={form.kling_duration} onValueChange={v => setForm(prev => ({ ...prev, kling_duration: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="kling-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="5">5 saniye</SelectItem>
                  <SelectItem value="10">10 saniye</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">En-Boy Oranı</Label>
              <Select value={form.kling_aspect_ratio} onValueChange={v => setForm(prev => ({ ...prev, kling_aspect_ratio: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="kling-ratio-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="16:9">Yatay (16:9)</SelectItem>
                  <SelectItem value="9:16">Dikey (9:16)</SelectItem>
                  <SelectItem value="1:1">Kare (1:1)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Kalite Modu</Label>
              <Select value={form.kling_mode} onValueChange={v => setForm(prev => ({ ...prev, kling_mode: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="kling-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="std">Standart (Hızlı)</SelectItem>
                  <SelectItem value="pro">Pro (Yüksek Kalite)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Step3({ selectedMode, models, setModels, providers }) {
  const needsText = !['runway_i2v', 'kling_i2v', 'merge'].includes(selectedMode);
  const needsImage = ['pipeline'].includes(selectedMode);
  const needsAudio = ['pipeline'].includes(selectedMode);

  // Helper: prefer Emergent models (no "direct" in id) as default
  const preferEmergent = (options) => {
    const emergent = options.find(o => !o.id?.includes('direct'));
    return emergent?.id || options[0]?.id || '';
  };

  const modelSections = [];
  if (needsText) {
    const opts = providers?.text?.models || [{ id: 'openai/gpt-5.2', label: 'GPT-5.2' }];
    modelSections.push({
      id: 'text', label: 'Senaryo / Prompt Modeli', desc: 'AI metin üretimi için',
      icon: FileText, color: 'violet',
      options: opts, defaultId: preferEmergent(opts),
    });
  }
  if (needsImage) {
    const opts = providers?.image?.models || [{ id: 'openai/gpt-image-1', label: 'GPT Image 1' }];
    modelSections.push({
      id: 'image', label: 'Görsel Modeli', desc: 'Sahne görselleri için',
      icon: Image, color: 'blue',
      options: opts, defaultId: preferEmergent(opts),
    });
  }
  if (needsAudio) {
    modelSections.push({
      id: 'audio', label: 'Ses Modeli', desc: 'Seslendirme için',
      icon: Mic, color: 'green',
      options: [{ id: 'tts-1', label: 'OpenAI TTS-1' }, { id: 'tts-1-hd', label: 'OpenAI TTS-1 HD' }],
      defaultId: 'tts-1',
    });
  }

  // Always show video engine info
  const videoEngine = selectedMode === 'pipeline' ? 'FFmpeg' : selectedMode === 'merge' ? 'FFmpeg Birleştirme' : selectedMode === 'runway_v2v' ? 'Runway Gen 4 Aleph' : selectedMode.startsWith('kling') ? 'Kling AI v2' : 'Runway Gen 4.5';

  return (
    <div className="space-y-6" data-testid="video-step-models">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">AI modellerini yapılandırın</h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">Her aşama için kullanılacak modeli seçin</p>
      </div>

      {/* Video Engine Card (info only) */}
      <Card className="bg-[#141414] border-[#262626]">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/5 border border-amber-500/30 flex items-center justify-center">
              <Film size={16} className="text-amber-400" />
            </div>
            <div>
              <p className="text-sm text-white font-ibm font-medium">Video Motoru</p>
              <p className="text-[10px] text-[#525252]">{videoEngine}</p>
            </div>
            <Badge variant="outline" className="ml-auto bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">Sabit</Badge>
          </div>
        </CardContent>
      </Card>

      {modelSections.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-[#1A1A1A]">
          <CardContent className="p-6 text-center">
            <Cpu size={32} className="mx-auto text-[#262626] mb-3" />
            <p className="text-sm text-[#737373] font-ibm">Bu mod için ek model yapılandırması gerekmez</p>
            <p className="text-xs text-[#525252] font-ibm mt-1">Devam ederek detayları girebilirsiniz</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {modelSections.map(section => (
            <Card key={section.id} className="bg-[#141414] border-[#262626]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorMap[section.color]} flex items-center justify-center`}>
                    <section.icon size={16} />
                  </div>
                  <div>
                    <p className="text-sm text-white font-ibm font-medium">{section.label}</p>
                    <p className="text-[10px] text-[#525252]">{section.desc}</p>
                  </div>
                </div>
                <Select
                  value={models[section.id] || section.defaultId || ''}
                  onValueChange={v => setModels(prev => ({ ...prev, [section.id]: v }))}
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white h-9 text-sm" data-testid={`video-model-${section.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {section.options.map(o => <SelectItem key={o.id || o.model} value={o.id || o.model}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════ STEP 4: DETAILS ═══════════════════ */

function Step4({ selectedMode, form, setForm, estimate, imagePreview, setImagePreview, setImageFile }) {
  const isI2V = selectedMode === 'runway_i2v' || selectedMode === 'kling_i2v';
  const isV2V = selectedMode === 'runway_v2v';
  const isMerge = selectedMode === 'merge';
  const needsTopic = !isI2V && !isMerge || selectedMode === 'kling_i2v';

  const [libraryVideos, setLibraryVideos] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);

  // Load library videos for merge mode
  useEffect(() => {
    if (isMerge && libraryVideos.length === 0) {
      setLoadingLibrary(true);
      api.get('/content/library?type=video&limit=50').then(r => {
        setLibraryVideos((r.data?.items || r.data || []).filter(a => a.type === 'video' && a.url));
      }).catch(() => {}).finally(() => setLoadingLibrary(false));
    }
  }, [isMerge]);

  const mergeClips = form.merge_clips || [];

  const addMergeClip = (url, title = '') => {
    if (!url || mergeClips.includes(url)) return;
    setForm(prev => ({ ...prev, merge_clips: [...(prev.merge_clips || []), url], merge_clip_titles: { ...(prev.merge_clip_titles || {}), [url]: title } }));
  };

  const removeMergeClip = (url) => {
    setForm(prev => {
      const titles = { ...(prev.merge_clip_titles || {}) };
      delete titles[url];
      return { ...prev, merge_clips: (prev.merge_clips || []).filter(u => u !== url), merge_clip_titles: titles };
    });
  };

  const moveMergeClip = (fromIdx, toIdx) => {
    setForm(prev => {
      const clips = [...(prev.merge_clips || [])];
      const [moved] = clips.splice(fromIdx, 1);
      clips.splice(toIdx, 0, moved);
      return { ...prev, merge_clips: clips };
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6" data-testid="video-step-details">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">
          {isMerge ? 'Birleştirilecek videoları seçin' : isI2V ? 'Görseli yükleyin ve talimatları girin' : isV2V ? 'Kaynak video ve dönüşüm detayları' : 'İçerik detaylarını girin'}
        </h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">
          {isMerge ? 'Kütüphaneden veya URL ile video ekleyin, sıralayın' : isI2V ? 'Canlandırılacak görseli seçin' : isV2V ? 'Dönüştürülecek videoyu belirtin' : 'Videonuz için konu ve tercihleri belirleyin'}
        </p>
      </div>

      <div className="space-y-4">
        {/* Merge: Clip Selection UI */}
        {isMerge && (
          <div className="space-y-4">
            <Card className="bg-[#141414] border-[#262626]">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-[#A3A3A3] font-ibm text-sm">Birleştirilecek Videolar ({mergeClips.length})</Label>
                  <Badge variant="outline" className="text-[10px] text-[#525252] border-[#262626]">En az 2 video gerekli</Badge>
                </div>

                {/* Selected clips */}
                {mergeClips.length > 0 && (
                  <div className="space-y-2">
                    {mergeClips.map((url, idx) => (
                      <div key={url} className="flex items-center gap-2 p-2 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A]" data-testid={`merge-clip-${idx}`}>
                        <span className="text-xs font-outfit font-bold text-amber-400 w-6 text-center">{idx + 1}</span>
                        <video src={`${API_URL}${url}`} className="w-16 h-10 rounded object-cover border border-[#262626]" muted />
                        <span className="text-xs text-white font-ibm flex-1 truncate">{(form.merge_clip_titles || {})[url] || url.split('/').pop()}</span>
                        <div className="flex gap-1">
                          {idx > 0 && (
                            <Button variant="ghost" size="sm" onClick={() => moveMergeClip(idx, idx - 1)} className="h-6 w-6 p-0 text-[#525252] hover:text-white">
                              <ArrowLeft size={10} />
                            </Button>
                          )}
                          {idx < mergeClips.length - 1 && (
                            <Button variant="ghost" size="sm" onClick={() => moveMergeClip(idx, idx + 1)} className="h-6 w-6 p-0 text-[#525252] hover:text-white">
                              <ArrowRight size={10} />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => removeMergeClip(url)} className="h-6 w-6 p-0 text-[#525252] hover:text-red-400">
                            <Trash2 size={10} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Manual URL input */}
                <div className="flex gap-2">
                  <Input
                    id="merge-manual-url"
                    placeholder="/api/media/video/... veya tam URL"
                    className="bg-[#0A0A0A] border-[#262626] text-white h-8 text-xs flex-1"
                    data-testid="merge-manual-url"
                    onKeyDown={e => { if (e.key === 'Enter') { addMergeClip(e.target.value); e.target.value = ''; } }}
                  />
                  <Button size="sm" onClick={() => {
                    const input = document.getElementById('merge-manual-url');
                    if (input?.value) { addMergeClip(input.value); input.value = ''; }
                  }} className="bg-amber-600 hover:bg-amber-500 text-white h-8 text-xs" data-testid="merge-add-url-btn">
                    <Plus size={12} className="mr-1" /> Ekle
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Library videos */}
            <Card className="bg-[#141414] border-[#262626]">
              <CardContent className="p-5 space-y-3">
                <Label className="text-[#A3A3A3] font-ibm text-sm">Kütüphaneden Seç</Label>
                {loadingLibrary ? (
                  <div className="flex items-center gap-2 py-4 justify-center">
                    <Loader2 size={14} className="animate-spin text-amber-400" />
                    <span className="text-xs text-[#525252] font-ibm">Yükleniyor...</span>
                  </div>
                ) : libraryVideos.length === 0 ? (
                  <p className="text-xs text-[#525252] font-ibm py-4 text-center">Kütüphanede video bulunamadı</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[300px] overflow-y-auto">
                    {libraryVideos.map(v => {
                      const isSelected = mergeClips.includes(v.url);
                      return (
                        <button key={v.id} onClick={() => isSelected ? removeMergeClip(v.url) : addMergeClip(v.url, v.title)}
                          data-testid={`library-video-${v.id}`}
                          className={`relative p-2 rounded-lg border text-left transition-all ${
                            isSelected ? 'bg-amber-500/10 border-amber-500/30' : 'bg-[#0A0A0A] border-[#1A1A1A] hover:border-[#404040]'
                          }`}>
                          <video src={`${API_URL}${v.url}`} className="w-full h-16 rounded object-cover" muted />
                          <p className="text-[10px] text-white font-ibm mt-1 truncate">{v.title}</p>
                          {isSelected && (
                            <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                              <Check size={10} className="text-black" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* I2V: Image Upload */}
        {isI2V && (
          <Card className="bg-[#141414] border-[#262626]">
            <CardContent className="p-5 space-y-3">
              <Label className="text-[#A3A3A3] font-ibm text-sm">Görsel Yükle *</Label>
              <div className="border-2 border-dashed border-[#262626] rounded-xl p-6 text-center hover:border-amber-500/30 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  data-testid="video-image-upload"
                  className="hidden"
                  id="video-image-upload"
                />
                <label htmlFor="video-image-upload" className="cursor-pointer">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Yüklenen" className="max-h-48 mx-auto rounded-lg border border-[#262626]" />
                  ) : (
                    <>
                      <Upload size={32} className="mx-auto text-[#525252] mb-2" />
                      <p className="text-sm text-[#737373] font-ibm">Tıklayarak görsel seçin</p>
                      <p className="text-xs text-[#525252] font-ibm mt-1">PNG, JPG, WEBP desteklenir</p>
                    </>
                  )}
                </label>
              </div>
              {imagePreview && (
                <Button variant="outline" size="sm" onClick={() => { setImagePreview(null); setImageFile(null); }} className="border-[#262626] text-[#A3A3A3] text-xs">
                  Görseli Kaldır
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* V2V: Source Video URL */}
        {isV2V && (
          <div>
            <Label className="text-[#A3A3A3] font-ibm text-sm">Kaynak Video URL *</Label>
            <Input
              value={form.source_video_url}
              onChange={e => setForm(prev => ({ ...prev, source_video_url: e.target.value }))}
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
              data-testid="video-source-url"
              placeholder="https://... veya /api/media/video/..."
            />
          </div>
        )}

        {/* Topic */}
        {needsTopic && (
          <div>
            <Label className="text-[#A3A3A3] font-ibm text-sm">
              {isV2V ? 'Dönüşüm Talimatı *' : 'Konu / Hedef *'}
            </Label>
            <Textarea
              value={form.topic}
              onChange={e => setForm(prev => ({ ...prev, topic: e.target.value }))}
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1 min-h-[80px]"
              data-testid="video-topic-input"
              placeholder={isV2V
                ? 'ör. Gece sahnesine dönüştür, neon ışıklar ekle'
                : 'ör. Yapay zeka ile iş süreçlerini hızlandırma'}
            />
          </div>
        )}

        {/* I2V: Animation instruction */}
        {isI2V && (
          <div>
            <Label className="text-[#A3A3A3] font-ibm text-sm">Animasyon Talimatı</Label>
            <Input
              value={form.topic}
              onChange={e => setForm(prev => ({ ...prev, topic: e.target.value }))}
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
              data-testid="video-i2v-prompt"
              placeholder="ör. Kamera yavaşça yaklaşsın, ışıklar değişsin"
            />
          </div>
        )}

        {/* Tone & Language (hide for I2V) */}
        {!isI2V && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Ton</Label>
              <Select value={form.tone} onValueChange={v => setForm(prev => ({ ...prev, tone: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="video-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {TONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Dil</Label>
              <Input
                value={form.language}
                onChange={e => setForm(prev => ({ ...prev, language: e.target.value }))}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                data-testid="video-language-input"
              />
            </div>
          </div>
        )}

        {/* Custom Instructions */}
        <div>
          <Label className="text-[#A3A3A3] font-ibm text-sm">Ek Talimatlar</Label>
          <Textarea
            value={form.custom_instructions}
            onChange={e => setForm(prev => ({ ...prev, custom_instructions: e.target.value }))}
            className="bg-[#0A0A0A] border-[#262626] text-white mt-1 min-h-[60px]"
            data-testid="video-instructions"
            placeholder="AI'ya özel talimatlar..."
          />
        </div>
      </div>

      {/* Credit Estimate */}
      {estimate && (
        <Card className={`border ${estimate.sufficient ? 'bg-[#141414] border-[#262626]' : 'bg-red-500/5 border-red-500/20'}`} data-testid="video-credit-estimate">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#A3A3A3] font-ibm flex items-center gap-1.5">
                <Coins size={12} className="text-[#FACC15]" /> Tahmini Maliyet
              </span>
              <Badge variant="outline" className="bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/20 font-jetbrains">
                {estimate.estimated_cost} kredi
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#525252] font-ibm">Bakiye: {estimate.available_credits}</span>
              <span className={`text-xs font-jetbrains font-bold ${estimate.sufficient ? 'text-green-400' : 'text-red-400'}`}>
                {estimate.sufficient ? `Kalan: ${estimate.remaining_after}` : 'Yetersiz bakiye'}
              </span>
            </div>
            {!estimate.sufficient && (
              <div className="flex items-center gap-1.5 mt-2 text-red-400">
                <AlertTriangle size={12} /><span className="text-xs font-ibm">Yetersiz kredi bakiyesi</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ═══════════════════ STEP 5: GENERATION & MONITORING ═══════════════════ */

function Step5({ selectedMode, generating, job, result, campaignJobs, backendUrl }) {
  const streamUrl = (url) => url ? `${backendUrl}${url.replace('/api/media/', '/api/media/stream/')}` : '';

  const progressLabel = () => {
    if (!job && !generating) return '';
    const p = job?.progress || 0;
    if (['runway', 'runway_i2v'].includes(selectedMode))
      return p < 30 ? 'Hazırlanıyor...' : p < 90 ? 'Runway AI video oluşturuyor...' : 'Tamamlanıyor...';
    if (selectedMode === 'merge')
      return 'FFmpeg ile birleştiriliyor...';
    if (selectedMode.startsWith('kling'))
      return p < 20 ? 'Prompt hazırlanıyor...' : p < 90 ? 'Kling AI video oluşturuyor...' : 'Tamamlanıyor...';
    if (selectedMode === 'runway_batch')
      return p < 15 ? 'Sahneler yazılıyor...' : p < 85 ? 'Klipler üretiliyor...' : p < 95 ? 'Birleştiriliyor...' : 'Tamamlanıyor...';
    if (selectedMode === 'runway_v2v')
      return p < 20 ? 'Prompt hazırlanıyor...' : p < 90 ? 'Runway dönüştürüyor...' : 'Tamamlanıyor...';
    return p < 20 ? 'Senaryo yazılıyor...' : p < 50 ? 'Görseller üretiliyor...' : p < 70 ? 'Seslendirme...' : p < 90 ? 'Birleştiriliyor...' : 'Tamamlanıyor...';
  };

  // Failed state
  if (!generating && !result && job?.status === 'failed') {
    return (
      <div className="space-y-6" data-testid="video-step-failed">
        <div>
          <h2 className="font-outfit text-xl font-bold text-white">Video üretimi başarısız</h2>
          <p className="text-sm text-[#737373] font-ibm mt-1">Bir hata oluştu, lütfen tekrar deneyin</p>
        </div>
        <Card className="bg-red-500/5 border-red-500/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle size={20} className="text-red-400" />
              <span className="text-sm text-red-400 font-ibm font-medium">Hata</span>
            </div>
            <p className="text-sm text-[#A3A3A3] font-ibm">{job.error_message || 'Bilinmeyen bir hata oluştu'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Campaign mode
  if (selectedMode === 'campaign' && campaignJobs) {
    const allDone = campaignJobs.every(j => j.status === 'done' || j.status === 'failed');
    return (
      <div className="space-y-6" data-testid="video-step-campaign-progress">
        <div>
          <h2 className="font-outfit text-xl font-bold text-white">
            {allDone ? 'Kampanya videoları hazır' : 'Kampanya videoları üretiliyor...'}
          </h2>
          <p className="text-sm text-[#737373] font-ibm mt-1">5 platform için video üretimi</p>
        </div>
        <div className="space-y-3">
          {campaignJobs.map((j, i) => {
            let videoUrl = null;
            if (j.result) { try { videoUrl = JSON.parse(j.result)?.video_url; } catch {} }
            return (
              <Card key={j.id || i} className="bg-[#141414] border-[#262626]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-ibm text-white">{j.platform}</span>
                    <Badge variant="outline" className={`text-xs ${
                      j.status === 'done' ? 'text-green-400 border-green-500/20' :
                      j.status === 'failed' ? 'text-red-400 border-red-500/20' :
                      'text-amber-400 border-amber-500/20'
                    }`}>
                      {j.status === 'done' ? 'Tamamlandı' : j.status === 'failed' ? 'Başarısız' : `%${j.progress || 0}`}
                    </Badge>
                  </div>
                  {videoUrl && (
                    <video controls className="w-full rounded-lg border border-[#262626] max-h-[160px]">
                      <source src={streamUrl(videoUrl)} type="video/mp4" />
                    </video>
                  )}
                  {!videoUrl && j.status !== 'done' && j.status !== 'failed' && (
                    <Progress value={j.progress || 5} className="h-1.5" />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Single video modes
  if (generating && !result) {
    return (
      <div className="space-y-6" data-testid="video-step-generating">
        <div>
          <h2 className="font-outfit text-xl font-bold text-white">Video üretiliyor...</h2>
          <p className="text-sm text-[#737373] font-ibm mt-1">AI çalışıyor, lütfen bekleyin</p>
        </div>
        <Card className="bg-[#141414] border-[#262626]">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <Loader2 size={20} className="animate-spin text-amber-400" />
              <span className="text-sm text-[#A3A3A3] font-ibm">{progressLabel()}</span>
            </div>
            <Progress value={job?.progress || 5} className="h-2" />
            <p className="text-xs text-[#525252] font-ibm">Video üretimi birkaç dakika sürebilir</p>
            {/* Pipeline steps visualization */}
            {selectedMode === 'pipeline' && (
              <div className="grid grid-cols-4 gap-2 mt-4">
                {[
                  { label: 'Senaryo', icon: FileText, threshold: 20 },
                  { label: 'Görseller', icon: Image, threshold: 50 },
                  { label: 'Ses', icon: Mic, threshold: 70 },
                  { label: 'Montaj', icon: Film, threshold: 90 },
                ].map(s => {
                  const p = job?.progress || 0;
                  const done = p >= s.threshold;
                  const active = p >= s.threshold - 20 && p < s.threshold;
                  return (
                    <div key={s.label} className={`p-3 rounded-lg border text-center ${
                      done ? 'bg-green-500/10 border-green-500/20' :
                      active ? 'bg-amber-500/10 border-amber-500/20' :
                      'bg-[#0A0A0A] border-[#1A1A1A]'
                    }`}>
                      <s.icon size={16} className={`mx-auto mb-1 ${
                        done ? 'text-green-400' : active ? 'text-amber-400' : 'text-[#525252]'
                      }`} />
                      <p className={`text-[10px] font-ibm ${done ? 'text-green-400' : active ? 'text-amber-400' : 'text-[#525252]'}`}>
                        {s.label}
                      </p>
                      {active && <Loader2 size={10} className="mx-auto mt-1 animate-spin text-amber-400" />}
                      {done && <Check size={10} className="mx-auto mt-1 text-green-400" />}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Result ready
  if (result) {
    return (
      <div className="space-y-6" data-testid="video-step-result">
        <div>
          <h2 className="font-outfit text-xl font-bold text-white">Video hazır</h2>
          <p className="text-sm text-[#737373] font-ibm mt-1">Sonucu gözden geçirin</p>
        </div>

        {/* Main Video */}
        {result.video_url && (
          <Card className="bg-[#0A0A0A] border-[#262626] overflow-hidden">
            <CardContent className="p-0">
              <video controls className="w-full max-h-[400px]" data-testid="video-result-player">
                <source src={streamUrl(result.video_url)} type="video/mp4" />
              </video>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <div className="flex flex-wrap gap-2">
          {result.dimensions && <Badge variant="outline" className="text-[#A3A3A3] border-[#262626] text-xs">{result.dimensions}</Badge>}
          {result.duration > 0 && <Badge variant="outline" className="text-[#A3A3A3] border-[#262626] text-xs"><Clock size={10} className="mr-1" />{Math.round(result.duration)}s</Badge>}
          {result.clip_count > 1 && <Badge variant="outline" className="text-blue-400 border-blue-500/20 text-xs">{result.clip_count} klip</Badge>}
          {result.models_used && Object.entries(result.models_used).map(([k, v]) => (
            <Badge key={k} variant="outline" className="bg-blue-500/5 text-blue-400 border-blue-500/20 text-xs font-jetbrains">
              <Cpu size={10} className="mr-1" />{k}: {v}
            </Badge>
          ))}
        </div>

        {/* Individual Clips (batch mode) */}
        {result.clip_urls?.length > 1 && (
          <div>
            <p className="text-xs text-[#525252] font-ibm uppercase tracking-wider mb-2">Tekil Klipler</p>
            <div className="grid grid-cols-2 gap-2">
              {result.clip_urls.map((url, i) => (
                <video key={i} controls className="w-full rounded-lg border border-[#262626] h-28 object-cover">
                  <source src={streamUrl(url)} type="video/mp4" />
                </video>
              ))}
            </div>
          </div>
        )}

        {/* Hero Image */}
        {result.hero_image_url && (
          <div>
            <p className="text-xs text-[#525252] font-ibm uppercase tracking-wider mb-2">Başlangıç Görseli</p>
            <img src={`${backendUrl}${result.hero_image_url}`} alt="" className="w-40 rounded-lg border border-[#262626]" />
          </div>
        )}

        {/* Script */}
        {result.script && (
          <div>
            <p className="text-xs text-[#525252] font-ibm uppercase tracking-wider mb-2">Senaryo / Prompt</p>
            <pre className="text-xs text-[#A3A3A3] font-ibm bg-[#0A0A0A] p-4 rounded-xl border border-[#1A1A1A] whitespace-pre-wrap max-h-[200px] overflow-y-auto" data-testid="video-result-script">
              {result.script}
            </pre>
          </div>
        )}
      </div>
    );
  }

  // Empty state
  return (
    <div className="text-center py-16" data-testid="video-step-empty">
      <Film size={48} className="mx-auto text-[#262626] mb-4" />
      <p className="text-[#737373] font-ibm text-sm">Üretim başlatılıyor...</p>
    </div>
  );
}

/* ═══════════════════ STEP 6: PREVIEW ═══════════════════ */

function Step6({ result, campaignJobs, selectedMode, backendUrl }) {
  const streamUrl = (url) => url ? `${backendUrl}${url.replace('/api/media/', '/api/media/stream/')}` : '';

  return (
    <div className="space-y-6" data-testid="video-step-preview">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">Video Önizleme</h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">Videonuzun son halini kontrol edin</p>
      </div>

      {/* Campaign Preview */}
      {selectedMode === 'campaign' && campaignJobs && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {campaignJobs.filter(j => j.status === 'done').map((j, i) => {
            let videoUrl = null;
            if (j.result) { try { videoUrl = JSON.parse(j.result)?.video_url; } catch {} }
            return videoUrl ? (
              <Card key={j.id || i} className="bg-[#0A0A0A] border-[#262626] overflow-hidden">
                <CardContent className="p-0">
                  <video controls className="w-full max-h-[200px]">
                    <source src={streamUrl(videoUrl)} type="video/mp4" />
                  </video>
                  <div className="p-3">
                    <p className="text-xs text-white font-ibm font-medium">{j.platform}</p>
                    <p className="text-[10px] text-[#525252]">{j.ratio}</p>
                  </div>
                </CardContent>
              </Card>
            ) : null;
          })}
        </div>
      )}

      {/* Single Video Preview */}
      {result?.video_url && selectedMode !== 'campaign' && (
        <Card className="bg-[#0A0A0A] border-[#262626]">
          <CardContent className="p-6">
            <div className="max-w-2xl mx-auto">
              <video controls className="w-full rounded-xl border border-[#1A1A1A]" data-testid="video-preview-player">
                <source src={streamUrl(result.video_url)} type="video/mp4" />
              </video>
              <div className="flex flex-wrap gap-2 mt-4 justify-center">
                {result.dimensions && <Badge variant="outline" className="text-[#A3A3A3] border-[#262626] text-xs">{result.dimensions}</Badge>}
                {result.duration > 0 && <Badge variant="outline" className="text-[#A3A3A3] border-[#262626] text-xs">{Math.round(result.duration)}s</Badge>}
                {result.aspect_ratio && <Badge variant="outline" className="text-amber-400 border-amber-500/20 text-xs">{result.aspect_ratio}</Badge>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!result?.video_url && !(selectedMode === 'campaign' && campaignJobs?.some(j => j.status === 'done')) && (
        <div className="text-center py-12">
          <Eye size={32} className="mx-auto text-[#262626] mb-3" />
          <p className="text-sm text-[#737373] font-ibm">Önizlenecek video bulunamadı</p>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════ STEP 7: DOWNLOAD & SHARE ═══════════════════ */

function Step7({ result, campaignJobs, selectedMode, onSave, backendUrl }) {
  const streamUrl = (url) => url ? `${backendUrl}${url.replace('/api/media/', '/api/media/stream/')}` : '';

  const downloadVideo = (url) => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = `${backendUrl}${url}`;
    a.download = 'video.mp4';
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="video-step-share">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">İndirin ve Paylaşın</h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">Videonuzu indirin veya kütüphaneye kaydedin</p>
      </div>

      <div className="space-y-3">
        {/* Download */}
        {result?.video_url && (
          <Card
            className="bg-[#141414] border-[#262626] hover:border-amber-500/20 transition-all cursor-pointer"
            onClick={() => downloadVideo(result.video_url)}
            data-testid="video-download-btn"
          >
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Download size={22} className="text-amber-400" />
              </div>
              <div>
                <p className="text-white font-ibm font-medium text-sm">Videoyu İndir</p>
                <p className="text-xs text-[#525252] mt-0.5">MP4 formatında bilgisayarınıza kaydedin</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Campaign Downloads */}
        {selectedMode === 'campaign' && campaignJobs && (
          <Card className="bg-[#141414] border-[#262626]">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <Download size={18} className="text-amber-400" />
                <p className="text-white font-ibm font-medium text-sm">Kampanya Videoları</p>
              </div>
              <div className="space-y-2">
                {campaignJobs.filter(j => j.status === 'done').map((j, i) => {
                  let videoUrl = null;
                  if (j.result) { try { videoUrl = JSON.parse(j.result)?.video_url; } catch {} }
                  return videoUrl ? (
                    <button
                      key={j.id || i}
                      onClick={() => downloadVideo(videoUrl)}
                      className="w-full flex items-center justify-between p-2 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A] hover:border-amber-500/20 transition-all text-left"
                    >
                      <span className="text-xs text-white font-ibm">{j.platform}</span>
                      <Download size={12} className="text-amber-400" />
                    </button>
                  ) : null;
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Save to Library */}
        <Card
          className="bg-[#141414] border-[#262626] hover:border-green-500/20 transition-all cursor-pointer"
          onClick={onSave}
          data-testid="video-save-library-btn"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <Save size={22} className="text-green-400" />
            </div>
            <div>
              <p className="text-white font-ibm font-medium text-sm">Medya Kütüphanesine Kaydet</p>
              <p className="text-xs text-[#525252] mt-0.5">Videoyu kütüphanenize ekleyin</p>
            </div>
          </CardContent>
        </Card>

        {/* Send to Social */}
        <Card className="bg-[#141414] border-[#262626] hover:border-violet-500/20 transition-all cursor-pointer" data-testid="video-share-social-btn">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
              <Send size={22} className="text-violet-400" />
            </div>
            <div>
              <p className="text-white font-ibm font-medium text-sm">Sosyal Medyada Paylaş</p>
              <p className="text-xs text-[#525252] mt-0.5">Bağlı platformlarınıza gönderin</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ═══════════════════ MAIN VIDEO STUDIO ═══════════════════ */

export default function VideoCreatorPage() {
  const [step, setStep] = useState(1);
  const [selectedMode, setSelectedMode] = useState('kling_t2v');
  const [models, setModels] = useState({});
  const [providers, setProviders] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [form, setForm] = useState({
    topic: '', platform: 'instagram_reel', tone: 'Profesyonel',
    voice: 'nova', language: 'Türkçe', scene_count: 3,
    custom_instructions: '', runway_ratio: '1280:720',
    runway_duration: 5, clip_count: 3, source_video_url: '',
    kling_model: 'kling-v3', kling_duration: '5',
    kling_aspect_ratio: '16:9', kling_mode: 'std',
    merge_clips: [], merge_clip_titles: {}, merge_aspect_ratio: '16:9',
  });

  // Media state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const [campaignJobs, setCampaignJobs] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPolling(), []);

  // Load providers
  useEffect(() => { api.get('/providers').then(r => setProviders(r.data)).catch(() => {}); }, []);

  // Estimate credits
  useEffect(() => {
    if (selectedMode === 'merge') { setEstimate({ sufficient: true, estimated_cost: 0, available_credits: 0, remaining_after: 0 }); return; }
    const creditMap = {
      runway: 'runway_video', runway_batch: 'runway_batch',
      runway_i2v: 'runway_video', runway_v2v: 'runway_v2v',
      pipeline: 'video_generation', campaign: 'runway_video',
    };
    const ct = creditMap[selectedMode] || 'video_generation';
    api.post('/credits/estimate', { generation_types: [ct] }).then(r => {
      const est = { ...r.data };
      if (selectedMode === 'runway_batch') {
        est.estimated_cost *= form.clip_count;
        est.remaining_after = est.available_credits - est.estimated_cost;
        est.sufficient = est.remaining_after >= 0;
      }
      if (selectedMode === 'campaign') {
        est.estimated_cost *= 5;
        est.remaining_after = est.available_credits - est.estimated_cost;
        est.sufficient = est.remaining_after >= 0;
      }
      setEstimate(est);
    }).catch(() => {});
  }, [selectedMode, form.clip_count]);

  const pollJob = useCallback((jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/jobs/${jobId}`);
        setJob(res.data);
        if (res.data.status === 'done') {
          stopPolling();
          setGenerating(false);
          try { setResult(JSON.parse(res.data.result)); } catch { setResult({ text: res.data.result }); }
          toast.success('Video oluşturuldu');
        } else if (res.data.status === 'failed') {
          stopPolling();
          setGenerating(false);
          toast.error(res.data.error_message || 'Video oluşturma başarısız');
        }
      } catch { stopPolling(); setGenerating(false); }
    }, 3000);
  }, []);

  const handleGenerate = async () => {
    // Validations
    if (selectedMode === 'merge') {
      if (!form.merge_clips || form.merge_clips.length < 2) { toast.error('En az 2 video seçin'); return; }
    } else if ((selectedMode === 'runway_i2v' || selectedMode === 'kling_i2v') && !imagePreview) { toast.error('Lütfen bir görsel yükleyin'); return; }
    else if (selectedMode === 'runway_v2v' && !form.source_video_url) { toast.error('Kaynak video URL gerekli'); return; }
    else if (!form.topic && selectedMode !== 'runway_i2v') { toast.error('Lütfen bir konu girin'); return; }
    if (estimate && !estimate.sufficient && selectedMode !== 'merge') { toast.error('Yetersiz kredi'); return; }

    setGenerating(true);
    setResult(null);
    setJob(null);
    setCampaignJobs(null);
    setStep(5);

    try {
      // Merge mode - direct FFmpeg merge, no job system
      if (selectedMode === 'merge') {
        try {
          const res = await api.post('/generate/video/merge', {
            clip_urls: form.merge_clips,
            aspect_ratio: form.merge_aspect_ratio || '16:9',
          });
          setResult(res.data);
          setGenerating(false);
          toast.success('Videolar birleştirildi!');
          setStep(6);
        } catch (err) {
          setGenerating(false);
          toast.error(err.response?.data?.detail || 'Birleştirme başarısız');
          setJob({ status: 'failed', error_message: err.response?.data?.detail || 'Birleştirme başarısız' });
        }
        return;
      }

      // Campaign mode uses separate endpoint
      if (selectedMode === 'campaign') {
        const textModel = models.text || 'openai/gpt-5.2';
        const [text_provider, text_model] = textModel.includes('/') ? textModel.split('/') : ['openai', textModel];
        const res = await api.post('/generate/video/campaign', {
          topic: form.topic, tone: form.tone, language: form.language,
          custom_instructions: form.custom_instructions,
          text_model, text_provider,
          runway_duration: form.runway_duration,
        });
        setCampaignJobs(res.data.jobs);
        // Poll all campaign jobs
        const jobIds = res.data.jobs.map(j => j.id);
        pollRef.current = setInterval(async () => {
          try {
            const results = await Promise.all(jobIds.map(id => api.get(`/jobs/${id}`)));
            const jobData = results.map(r => r.data);
            const allDone = jobData.every(j => j.status === 'done' || j.status === 'failed');
            setCampaignJobs(prev => prev.map((pj, i) => ({
              ...pj,
              status: jobData[i]?.status || pj.status,
              progress: jobData[i]?.progress || pj.progress,
              result: jobData[i]?.result || pj.result,
            })));
            if (allDone) {
              stopPolling();
              setGenerating(false);
              toast.success('Kampanya videoları hazır');
            }
          } catch { stopPolling(); setGenerating(false); }
        }, 5000);
        return;
      }

      // Standard video generation
      const textModel = models.text || 'openai/gpt-5.2';
      const [text_provider, text_model] = textModel.includes('/') ? textModel.split('/') : ['openai', textModel];
      const imageModel = models.image || 'gpt-image-1';

      const payload = {
        topic: form.topic,
        platform: form.platform,
        aspect_ratio: PLATFORMS.find(p => p.value === form.platform)?.ratio || '9:16',
        tone: form.tone,
        voice: form.voice,
        language: form.language,
        scene_count: form.scene_count,
        custom_instructions: form.custom_instructions,
        text_model, text_provider,
        image_model: imageModel,
        audio_model: models.audio || 'tts-1',
        video_mode: selectedMode,
        runway_ratio: form.runway_ratio,
        runway_duration: form.runway_duration,
        clip_count: form.clip_count,
        image_data_uri: (selectedMode === 'runway_i2v' || selectedMode === 'kling_i2v') ? imagePreview : null,
        source_video_url: form.source_video_url,
        kling_model: form.kling_model,
        kling_duration: form.kling_duration,
        kling_aspect_ratio: form.kling_aspect_ratio,
        kling_mode: form.kling_mode,
      };

      const res = await api.post('/generate/video', payload);
      setJob(res.data);
      pollJob(res.data.id);
    } catch (err) {
      setGenerating(false);
      toast.error(err.response?.data?.detail || 'Video oluşturma başarısız');
    }
  };

  const handleSave = async () => {
    if (!result?.video_url) { toast.error('Kaydedilecek video yok'); return; }
    try {
      await api.post('/content/library', {
        title: `Video - ${form.topic?.slice(0, 50) || selectedMode}`,
        content: result.script || '', type: 'video',
        url: result.video_url,
        metadata: {
          video_url: result.video_url, platform: form.platform,
          mode: selectedMode, duration: result.duration,
          poster: result.hero_image_url || null,
        },
      });
      toast.success('Medya kütüphanesine kaydedildi');
    } catch { toast.error('Kaydetme başarısız'); }
  };

  const canProceed = () => {
    if (step === 1) return !!selectedMode;
    if (step === 2) return true;
    if (step === 3) return true;
    if (step === 4) {
      if (selectedMode === 'merge') return (form.merge_clips || []).length >= 2;
      if (selectedMode === 'runway_i2v') return !!imagePreview;
      if (selectedMode === 'runway_v2v') return !!form.source_video_url && !!form.topic;
      return !!form.topic.trim();
    }
    if (step === 5) {
      if (selectedMode === 'campaign') return campaignJobs?.some(j => j.status === 'done') && !generating;
      return !!result && !generating;
    }
    if (step === 6) return true;
    return true;
  };

  const nextStep = () => {
    if (step === 4) { handleGenerate(); return; }
    if (step < 7) setStep(step + 1);
  };
  const prevStep = () => { if (step > 1) setStep(step - 1); };

  // Reset generation when mode changes
  const handleModeChange = (mode) => {
    setSelectedMode(mode);
    setResult(null);
    setJob(null);
    setCampaignJobs(null);
    setGenerating(false);
    stopPolling();
  };

  return (
    <div data-testid="video-creator-page" className="animate-fade-in">
      {/* Header */}
      <div className="pb-5 mb-5 border-b border-[#262626]">
        <h1 className="font-outfit text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-rose-500/20 flex items-center justify-center">
            <Film size={20} className="text-amber-400" />
          </div>
          Video Stüdyosu
        </h1>
        <p className="text-[#737373] font-ibm text-sm mt-1 ml-[52px]">Adım adım profesyonel AI video üretin</p>
      </div>

      {/* Stepper */}
      <Stepper currentStep={step} steps={STEPS} onStepClick={setStep} />

      {/* Step Content */}
      <div className="mt-6 min-h-[400px]">
        {step === 1 && <Step1 selectedMode={selectedMode} setSelectedMode={handleModeChange} />}
        {step === 2 && <Step2 selectedMode={selectedMode} form={form} setForm={setForm} providers={providers} />}
        {step === 3 && <Step3 selectedMode={selectedMode} models={models} setModels={setModels} providers={providers} />}
        {step === 4 && <Step4 selectedMode={selectedMode} form={form} setForm={setForm} estimate={estimate} imagePreview={imagePreview} setImagePreview={setImagePreview} setImageFile={setImageFile} />}
        {step === 5 && <Step5 selectedMode={selectedMode} generating={generating} job={job} result={result} campaignJobs={campaignJobs} backendUrl={API_URL} />}
        {step === 6 && <Step6 result={result} campaignJobs={campaignJobs} selectedMode={selectedMode} backendUrl={API_URL} />}
        {step === 7 && <Step7 result={result} campaignJobs={campaignJobs} selectedMode={selectedMode} onSave={handleSave} backendUrl={API_URL} />}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-5 border-t border-[#262626]">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={step === 1}
          className="border-[#262626] text-[#A3A3A3] hover:text-white h-10"
          data-testid="video-prev-step-btn"
        >
          <ArrowLeft size={14} className="mr-1.5" /> Geri
        </Button>

        <div className="flex items-center gap-2 text-xs text-[#525252] font-ibm">
          Adım {step} / {STEPS.length}
        </div>

        {step < 7 ? (
          <Button
            onClick={nextStep}
            disabled={!canProceed()}
            className="bg-amber-600 hover:bg-amber-500 text-white h-10 px-6"
            data-testid="video-next-step-btn"
          >
            {step === 4 ? (
              <>{generating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />} Üret ve Devam Et</>
            ) : (
              <>Devam <ArrowRight size={14} className="ml-1.5" /></>
            )}
          </Button>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
}
